"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { buildCaptchaOptions, isCaptchaEnabled, isCaptchaVerificationError, normalizeCaptchaToken } from "@/lib/auth/captcha";
import { ACTIVE_BRANDING } from "@/lib/branding";
import { createClient } from "@/lib/supabase/client";
import { Button, Input, Label } from "@handled-ai/design-system";
import { AuthPageShell } from "./auth-page-shell";
import { CaptchaWidget, type CaptchaWidgetRef } from "./captcha-widget";

export default function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const captchaRef = useRef<CaptchaWidgetRef>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setCaptchaError(null);

    const captchaRequired = isCaptchaEnabled();
    const normalizedToken = normalizeCaptchaToken(captchaToken);
    if (captchaRequired && !normalizedToken) {
      setCaptchaError("Complete the CAPTCHA and try again.");
      setLoading(false);
      return;
    }

    const supabase = createClient();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      ...buildCaptchaOptions(normalizedToken),
    });

    setCaptchaToken("");
    captchaRef.current?.reset();

    if (error) {
      console.warn(
        "[forgot-password-form] Supabase password reset request failed:",
        error.message,
      );

      if (captchaRequired && isCaptchaVerificationError(error)) {
        setCaptchaError("Complete the CAPTCHA and try again.");
        setLoading(false);
        return;
      }
    }

    setSubmitted(true);
    setLoading(false);
  }

  return (
    <AuthPageShell>
      <div className="space-y-2 text-center">
        <h1 className="text-xl font-medium tracking-tight">Reset your password</h1>
        <p className="text-sm text-muted-foreground">
          Enter the email you use for {ACTIVE_BRANDING.productName}. We’ll send a secure reset link.
        </p>
      </div>

      {submitted ? (
        <div className="space-y-4">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-sm text-emerald-800">
              If an account uses this email, we’ll send password reset instructions for {ACTIVE_BRANDING.productName}.
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            Not sure which email you used? Check the inbox where you received your invite, or contact your workspace admin for help.
          </p>
          <div className="text-center text-sm">
            <Link href="/login" className="text-foreground underline underline-offset-4 hover:text-foreground/80">
              Back to sign in
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recovery-email">Email</Label>
            <Input
              id="recovery-email"
              name="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="rounded-md border border-border bg-muted/30 p-3">
            <p className="text-sm text-muted-foreground">
              Forgot which email you use? Your email is your username. Try the email that received your invite, or contact your workspace admin.
            </p>
          </div>

          <CaptchaWidget
            ref={captchaRef}
            onVerify={(token) => {
              setCaptchaToken(token);
              setCaptchaError(null);
            }}
            onExpire={() => setCaptchaToken("")}
          />

          {captchaError && <p className="text-sm text-red-600">{captchaError}</p>}

          <Button type="submit" disabled={loading} className="w-full h-10">
            {loading ? "Sending..." : "Send reset instructions"}
          </Button>
        </form>
      )}
    </AuthPageShell>
  );
}
